import React, { FC, useCallback, useEffect, useReducer, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog, F0Select, F0SelectItemObject } from '@factorialco/f0-react'
import { Input, Textarea } from '@factorialco/f0-react/dist/experimental'
import {
  Bullet,
  Calendar,
  Check,
  ChevronDown,
  GroupBy,
  Link,
  Numbers,
  Star,
  TextSize,
} from '@factorialco/f0-react/icons/app'
import { FormsElementTypeEnum } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { elementFromData, getDefaultOptions } from 'modules/forms/hooks/useForms'
import { Element } from 'modules/forms/lib/Element'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'

interface Props {
  /** The element we want to edit, create new one if undefined */
  element?: Element
  /** Whether the element has a correct answer */
  hasCorrectAnswer?: boolean
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback to update or create the element  */
  onChange: (element: Element) => void
  /** Callback when modal is closed */
  onClose: () => void
  formType: string
  permittedFieldTypes?: FormsElementTypeEnum[]
  isCreatingInsideSection: boolean
}

const ElementEditor: FC<Props> = ({
  element,
  isOpen,
  onChange,
  onClose,
  formType,
  hasCorrectAnswer,
  isCreatingInsideSection,
  permittedFieldTypes,
}) => {
  // We store the values in a cloned element. When we update a field, the state does not change, so every time we call this function there is a state change to update the component with the values stored in the cloned element.
  const [, forceUpdate] = useReducer((x) => x + 1, 0)
  // function to clone the element
  const cloneElement = (el: Element): Element => {
    const newClonedElement = elementFromData({
      id: el.id,
      description: el.description,
      label: el.label,
      mandatory: el.mandatory,
      options: el.options,
      params: el.params,
      position: el.position,
      sectionId: el.sectionId || undefined,
      type: el.getType(),
      hasCorrectAnswer: el.hasCorrectAnswer,
    })
    newClonedElement.uuid = el.uuid
    if (newClonedElement instanceof SectionElement && el instanceof SectionElement) {
      newClonedElement.elements = el.elements
    }
    return newClonedElement
  }

  // function to create a default element for create mode
  const createDefaultElement = useCallback((): Element => {
    const type = formType === 'knowledge' ? 'select' : 'text'
    const allowedElements = permittedFieldTypes || []
    const defaultType = allowedElements.length > 0 && allowedElements[0] ? allowedElements[0] : type
    const defaultHasCorrectAnswer = formType === 'knowledge' || hasCorrectAnswer || false
    return elementFromData({
      type: defaultType,
      label: '',
      description: '',
      mandatory: formType === 'knowledge',
      options: getDefaultOptions(defaultType, defaultHasCorrectAnswer),
      params: {},
      position: 0,
      hasCorrectAnswer: defaultHasCorrectAnswer,
    })
  }, [formType, permittedFieldTypes, hasCorrectAnswer])

  // The cloned element to work with. If we update it, not one of its properties, it changes the state of the component and is updated.
  const [clonedElement, setClonedElement] = useState(() =>
    element ? cloneElement(element) : createDefaultElement()
  )

  // Track which fields have been touched/blurred to only show errors after user interaction
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  // If the element is changed (for example, the form is reloaded)
  useEffect(() => {
    setClonedElement(element ? cloneElement(element) : createDefaultElement())
    setTouchedFields(new Set()) // Reset touched fields when element changes
  }, [element, createDefaultElement])

  /** Function to change the element type */
  const changeElementType = (type: FormsElementTypeEnum) => {
    const shouldPreserveOptions = clonedElement.hasOptions() && Element.typeHasOptions(type)

    const options = shouldPreserveOptions
      ? clonedElement.options
      : getDefaultOptions(type, hasCorrectAnswer)

    const newElement = elementFromData({
      id: clonedElement.id,
      description: clonedElement.description,
      label: clonedElement.label,
      mandatory: type === 'section' ? false : clonedElement.mandatory,
      options: options,
      params: clonedElement.params,
      position: clonedElement.position,
      sectionId: clonedElement.sectionId || undefined,
      type: type,
      hasCorrectAnswer: clonedElement.hasCorrectAnswer,
    })

    setClonedElement(newElement)
  }

  /** function to prevent error: Type 'string' is not assignable to type 'FormsElementTypeEnum'. */
  const validFormsElementTypeEnum = (value: FormsElementTypeEnum): FormsElementTypeEnum => {
    return value
  }

  /** Returns the available options for changing the element type */
  const validTypesToChange = (): F0SelectItemObject<FormsElementTypeEnum>[] => {
    const allValidTypes = [
      {
        label: i18n.t('forms.modals.elements.single_choice'),
        icon: Bullet,
        value: validFormsElementTypeEnum('select'),
      },
      {
        label: i18n.t('forms.modals.elements.multiple_choice'),
        icon: Check,
        value: validFormsElementTypeEnum('multiselect'),
      },
    ]
    if (formType !== 'knowledge') {
      allValidTypes.push(
        {
          label: i18n.t('forms.modals.elements.rating'),
          icon: Star,
          value: validFormsElementTypeEnum('rating'),
        },
        {
          label: i18n.t('forms.modals.elements.numeric'),
          icon: Numbers,
          value: validFormsElementTypeEnum('number'),
        },
        {
          label: i18n.t('forms.modals.elements.text'),
          icon: TextSize,
          value: validFormsElementTypeEnum('text'),
        },
        {
          label: i18n.t('forms.modals.elements.url'),
          icon: Link,
          value: validFormsElementTypeEnum('url'),
        },
        {
          label: i18n.t('forms.modals.elements.dropdown'),
          icon: ChevronDown,
          value: validFormsElementTypeEnum('dropdownsingle'),
        },
        {
          label: i18n.t('forms.modals.elements.date'),
          icon: Calendar,
          value: validFormsElementTypeEnum('date'),
        }
      )
    }
    // we only can change to section if the element is not inside one
    if (!clonedElement.sectionId && !isCreatingInsideSection) {
      allValidTypes.push({
        label: i18n.t('forms.modals.elements.section'),
        icon: GroupBy,
        value: validFormsElementTypeEnum('section'),
      })
    }

    // Filter by permittedFieldTypes if provided
    if (permittedFieldTypes && permittedFieldTypes.length > 0) {
      return allValidTypes.filter((type) => permittedFieldTypes.includes(type.value))
    }

    return allValidTypes
  }

  const errors = clonedElement.validate()
  const isValid = Object.keys(errors).length === 0

  // Helper function to get error for a field (only show if field has been touched)
  const getFieldError = (fieldName: string): string | undefined => {
    return touchedFields.has(fieldName) ? errors[fieldName] : undefined
  }

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        !element // If no element, we are creating a new one
          ? i18n.t('forms.modals.actions.add_content')
          : i18n.t('forms.editor.modal_title')
      }
      primaryAction={{
        label: element ? i18n.t('common.edit') : i18n.t('common.add'),
        onClick: () => onChange(clonedElement),
        disabled: !isValid || !clonedElement.label.trim(),
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box className="gap-3">
        {/* gap-3 is the same that OneModal has */}

        <Box flexDirection="row" className="gap-3">
          <Box width="full">
            <F0Select<FormsElementTypeEnum>
              label={i18n.t('forms.editor.type_title')}
              options={validTypesToChange()}
              value={clonedElement.getType()}
              onChange={(value: FormsElementTypeEnum) => {
                changeElementType(value)
              }}
            />
          </Box>
          {formType !== 'knowledge' &&
            clonedElement.getType() !== 'section' &&
            !hasCorrectAnswer && ( // TODO: remove form_type check once we migrate knowledge forms to scorable
              <Box width="full">
                <F0Select
                  label={i18n.t('forms.editor.mandatory_title')}
                  options={[
                    {
                      label: i18n.t('common.yes'),
                      value: 'true',
                    },
                    {
                      label: i18n.t('common.no'),
                      value: 'false',
                    },
                  ]}
                  value={clonedElement.mandatory ? 'true' : 'false'}
                  onChange={(value: string) => {
                    clonedElement.mandatory = value === 'true'
                    // Changing an element value does not cause a state change, so we call 'forceUpdate'.
                    forceUpdate()
                  }}
                />
              </Box>
            )}
        </Box>

        <Input
          label={i18n.t('forms.editor.label_title')}
          error={getFieldError('label')}
          onChange={(value: string) => {
            clonedElement.label = value
            // Mark field as touched on first change
            if (!touchedFields.has('label')) {
              setTouchedFields((prev) => new Set(prev).add('label'))
            }
            // Changing an element value does not cause a state change, so we call 'forceUpdate'.
            forceUpdate()
          }}
          placeholder={i18n.t('forms.editor.label_placeholder')}
          value={clonedElement.label}
          required
        />

        <Textarea
          label={i18n.t('forms.editor.description_title')}
          onChange={(value: string) => {
            clonedElement.description = value
            // Changing an element value does not cause a state change, so we call 'forceUpdate'.
            forceUpdate()
          }}
          placeholder={i18n.t('forms.editor.description_placeholder')}
          value={clonedElement.description}
        />

        {clonedElement.params.optionalComment && (
          <F0Alert variant="info" title={i18n.t('forms.editor.optional_comment')} description="" />
        )}

        {clonedElement.getEditorComponent((updatedElement) => {
          setClonedElement(cloneElement(updatedElement))
        })}
      </Box>
    </F0Dialog>
  )
}
export default ElementEditor
